import { useEffect, useRef, useState, useCallback } from "react";
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawCircleMode,
  TerraDrawSelectMode,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

/**
 * Google removed the Drawing Library (DrawingManager) from the Maps JS API in
 * v3.65 (May 2026). This hook wires Terra Draw — Google's endorsed
 * replacement — onto a google.maps.Map instance.
 *
 * Shapes are stored as GeoJSON features. Use the returned helpers to switch
 * modes ("polygon", "circle", "select", "static") and react to events.
 *
 * @param {google.maps.Map|null} map - map instance (null until loaded)
 * @param {object} opts
 * @param {boolean} opts.enabled - only initialize when true
 * @param {function} opts.onFinish - (feature, draw) => void, fired when a shape is drawn
 * @param {function} opts.onSelect - (feature, draw) => void, fired when a shape is clicked in select mode
 * @param {object} opts.polygonStyles - Terra Draw polygon mode styles
 * @param {object} opts.circleStyles - Terra Draw circle mode styles
 * @param {object} opts.selectFlags - Terra Draw select mode flags (per mode name)
 */
export default function useTerraDraw(
  map,
  { enabled = true, onFinish, onSelect, polygonStyles, circleStyles, selectFlags } = {}
) {
  const drawRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Keep latest callbacks without re-initializing Terra Draw
  const onFinishRef = useRef(onFinish);
  const onSelectRef = useRef(onSelect);
  onFinishRef.current = onFinish;
  onSelectRef.current = onSelect;
  const polygonStylesRef = useRef(polygonStyles);
  const circleStylesRef = useRef(circleStyles);
  const selectFlagsRef = useRef(selectFlags);

  useEffect(() => {
    if (!enabled || !map || !window.google?.maps) return undefined;

    let cancelled = false;
    let listener = null;

    const init = () => {
      if (cancelled || drawRef.current) return;
      try {
        const draw = new TerraDraw({
          adapter: new TerraDrawGoogleMapsAdapter({
            map,
            lib: window.google.maps,
            coordinatePrecision: 9,
          }),
          modes: [
            new TerraDrawPolygonMode({
              styles: polygonStylesRef.current || {},
            }),
            new TerraDrawCircleMode({
              styles: circleStylesRef.current || {},
            }),
            new TerraDrawSelectMode({
              flags: selectFlagsRef.current || {
                polygon: { feature: {} },
                circle: { feature: {} },
              },
            }),
          ],
        });

        draw.on("ready", () => {
          if (!cancelled) setReady(true);
        });

        draw.on("finish", (id, context) => {
          // Only react to completed drawings, not drags/edits
          if (context?.action && context.action !== "draw") return;
          const feature = draw.getSnapshotFeature(id);
          if (feature && onFinishRef.current) onFinishRef.current(feature, draw);
        });

        draw.on("select", (id) => {
          const feature = draw.getSnapshotFeature(id);
          if (feature && onSelectRef.current) onSelectRef.current(feature, draw);
        });

        draw.start();
        drawRef.current = draw;
      } catch (e) {
        console.warn("Terra Draw initialization failed:", e);
      }
    };

    // The Google Maps adapter needs the map projection, which is only
    // available once tiles have started loading.
    if (map.getProjection()) {
      init();
    } else {
      listener = window.google.maps.event.addListenerOnce(
        map,
        "projection_changed",
        init
      );
    }

    return () => {
      cancelled = true;
      if (listener) window.google.maps.event.removeListener(listener);
      if (drawRef.current) {
        try {
          drawRef.current.stop();
        } catch (_) {
          /* already stopped */
        }
        drawRef.current = null;
      }
      setReady(false);
    };
  }, [enabled, map]);

  const setMode = useCallback((mode) => {
    try {
      drawRef.current?.setMode(mode);
    } catch (e) {
      console.warn(`Terra Draw: could not set mode "${mode}":`, e);
    }
  }, []);

  const clear = useCallback(() => {
    try {
      drawRef.current?.clear();
    } catch (_) {
      /* no-op */
    }
  }, []);

  return { draw: drawRef, ready, setMode, clear };
}

/** Convert a GeoJSON polygon feature to an array of { lat, lng } (closing point dropped). */
export function polygonFeatureToPath(feature) {
  const ring = feature?.geometry?.coordinates?.[0] ?? [];
  const openRing = ring.length > 1 ? ring.slice(0, ring.length - 1) : ring;
  return openRing.map(([lng, lat]) => ({ lat, lng }));
}

/** Convert a Terra Draw circle feature to { center: {lat,lng}, radius } (radius in meters). */
export function circleFeatureToCircle(feature) {
  const path = polygonFeatureToPath(feature);
  if (path.length === 0) return { center: { lat: 0, lng: 0 }, radius: 0 };

  const center = path.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat / path.length, lng: acc.lng + p.lng / path.length }),
    { lat: 0, lng: 0 }
  );

  const radiusKm = feature?.properties?.radiusKilometers;
  const radius = Number.isFinite(radiusKm)
    ? radiusKm * 1000
    : haversineMeters(center, path[0]);

  return { center, radius };
}

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
